import { useState } from 'react'
import axios from 'axios'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import Layout from '../../components/Layout'
import './style.css'
import ClientMultiselect from '../../components/ClientMultiselect'
import { useSelector } from 'react-redux'
import { API_BASE_URL } from '../../config/api'

// Casino providers/vendors — js_games ke distinct vendors ka static snapshot.
// Backend pe load na pade isliye realtime fetch nahi; DB values se exact match.
const CASINO_VENDORS = [
  '100hp', '18peaches', '2j', '5g', '759 gaming', '9game', '9w', 'ag', 'amigo', 'aog',
  'askmeslot', 'atg', 'atm', 'aviatrix', 'barbara bang', 'bgaming', 'biggaming', 'bng',
  'bng3oks', 'btgaming', 'bti', 'casini', 'casino game', 'cmd', 'cpgame', 'cq9',
  'creedroomz', 'crowdplay', 'cyberbetx', 'dpgaming', 'dpsports', 'dreamgaming',
  'eazygaming', 'eeai', 'endorphina', 'epicwin', 'esports', 'evo888h5',
  'evolutioin-bigtimegaming', 'evolutioin-netent', 'evolution', 'evolution live',
  'evolution live(asia)', 'evolution-nlc', 'evolution-redtiger', 'evoplay-aisa',
  'evoplay-eu', 'expanse', 'ezugi', 'fachaigaming', 'fastspin', 'funky games',
  'galaxsys', 'gameart', 'gamingsoft-ai live casino', 'gamingsoft-wow', 'habanero',
  'hacksaw', 'hacksaw asia', 'hacksaw latam', 'hacksaw world', 'ia', 'ideal', 'inout',
  'jdbgaming', 'jili', 'jiligaming', 'jilisweep', 'ka', 'kalamba', 'km', 'km-onegaming',
  'koolbet', 'ky', 'live22', 'lobby', 'mac88', 'mancala', 'microgaming', 'mini', 'mt',
  'netent', 'nextspin', 'odin', 'ongaming', 'onlyplay', 'penguinking', 'pgsgaming',
  'pgsoft', 'pix', 'playngo', 'playson', 'playtech', 'playtech(row)', 'pragmatic',
  'pragmaticlive', 'pragmaticplay-asia', 'pragmaticplay-eu', 'pragmaticplaylive-asia',
  'pragmaticplaylive-eu', 'psg', 'rectangle', 'relaxgaming', 'rg', 'rich paradise',
  'rich88', 'rubyplay', 'sabasports(ibc)', 'sagaming', 'sbo', 'sexy', 'sg(spadegaming)',
  'skywind', 'smartsoft', 'sports', 'spribe', 't1', 'tadagaming', 'topbet',
  'turbogames asia', 'turbogames world', 'unitedgaming', 'v8', 'va', 'veliplay', 'vplus',
  'wintolive-12.31起暂停开放', 'wintoslot-12.31起暂停开放', 'wm', 'wonwon', 'ws168',
  'yeebet', 'yellow bat', 'ygr',
]

/**
 * CasinoReport Component
 *
 * Page for viewing casino reports with filters and export options.
 */
function CasinoReport() {
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [searchValue, setSearchValue] = useState('')
  const [casinoType, setCasinoType] = useState('')
  const [selectOption, setSelectOption] = useState('')
  const [selectValue, setSelectValue] = useState('')
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const { user, token, role, isAuthenticated } = useSelector((state) => state.auth)

  // Handle Submit button click
  const handleSubmit = async () => {
    setLoading(true)
    try {
      // Get token based on active role (following app pattern)
      const activeRole = localStorage.getItem('activeRole')
      let token = null

      if (activeRole === 'owner') {
        token = localStorage.getItem('ownerToken')
      } else if (activeRole === 'staff') {
        token = localStorage.getItem('staffToken')
      } else {
        token = localStorage.getItem('token')
      }

      // V2: sirf staff/owner — scope backend token se leta hai, isliye role/
      // username bhejne ki zarurat nahi. Sirf vendor filter (optional).
      const payload = {
        vendor: selectValue
      }

      const response = await axios.post(
        `${API_BASE_URL}/admin/reports/casino-report-v2`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      )

      if (response && response.data && response.data.success && Array.isArray(response.data.data)) {
        const mapped = response.data.data.map((item) => ({
          id: item.id,
          gameName: item.game_name || item.game_name || '-',
          type: item.transaction_type || item.game_type || '-',
          amount: item.amount != null ? parseFloat(item.amount).toFixed(2) : '-',
          total: item.amount != null ? parseFloat(item.amount).toFixed(2) : '-',
          date: item.timestamp ? formatTimestamp(item.timestamp) : '-',
          roundId: item.serial_number || item.round_id || '-',
          transactionId: item.external_transaction_id || item.transaction_id || '-'
        }))

        setTableData(mapped)
      } else {
        setTableData([])
      }
    } catch (error) {
      console.error('Error loading data:', error)
      setTableData([])
    } finally {
      setLoading(false)
    }
  }

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new()
    const exportRows = [['Game Name', 'Type', 'Amount', 'Total', 'Date', 'Round Id', 'Transaction Id'],
    ...tableData.map(row => [
      row.gameName || '-',
      row.type || '-',
      row.amount || '-',
      row.total || '-',
      row.date || '-',
      row.roundId || '-',
      row.transactionId || '-'
    ])]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(exportRows), 'Report')
    XLSX.writeFile(wb, `CasinoReport_${casinoType || 'Report'}.xlsx`)
  }

  const handleExportPDF = () => {
    const doc = new jsPDF('landscape')
    doc.setFontSize(16)
    doc.text('Casino Report', 14, 15)
    doc.setFontSize(10)
    doc.text(`Type: ${casinoType || 'All'}`, 14, 22)
    doc.text(`Vendor: ${selectValue || 'All'}`, 14, 28)
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 34)

    autoTable(doc, {
      head: [['Game Name', 'Type', 'Amount', 'Total', 'Date', 'Round Id', 'Transaction Id']],
      body: tableData.map(row => [
        row.gameName || '-',
        row.type || '-',
        row.amount || '-',
        row.total || '-',
        row.date || '-',
        row.roundId || '-',
        row.transactionId || '-'
      ]),
      startY: 40,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [44, 62, 80] },
    })

    doc.save(`CasinoReport_${casinoType || 'Report'}.pdf`)
  }

  // Helper: format ISO timestamp to DD/MM/YYYY (or local date)
  const formatTimestamp = (ts) => {
    try {
      const d = new Date(ts)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    } catch (e) {
      return ts
    }
  }

  return (
    <Layout title="Casino Report - Diamond Admin">
      <div className="listing-grid">
        <div className="account-list-statement">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-flex align-items-center justify-content-between">
                <h4 className="mb-0 font-size-18">
                  Casino Report
                </h4>
                <div className="page-title-right">
                  <button
                    type="button"
                    className={`btn btn-success btn-sm ml-2 ${tableData.length > 0 ? '' : 'disabled'}`}
                    disabled={tableData.length === 0}
                    onClick={handleExportExcel}
                    title="Export to Excel"
                  >
                    <i className="fas fa-file-excel mr-1" /> Excel
                  </button>
                  <button
                    type="button"
                    className={`btn btn-danger btn-sm ml-2 ${tableData.length > 0 ? '' : 'disabled'}`}
                    disabled={tableData.length === 0}
                    onClick={handleExportPDF}
                    title="Export to PDF"
                  >
                    <i className="fas fa-file-pdf mr-1" /> PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body-casino">
              {/* Filter Form */}
              <div className="row">
                <div className="col-12">
                  <div className="">
                    <div className="">
                      <form method="post" className="ajaxFormSubmit" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                        <div className="row row5">
                          <div className="col-md-2">
                            {/* <label>Select Casino Type:</label> */}
                            <select
                              className="form-control"
                              value={casinoType}
                              onChange={(e) => setCasinoType(e.target.value)}
                            >
                              <option value="">Select Casino Type</option>
                              <option value="SettleBet">Settled Bets</option>
                              <option value="Unsettlebet">UnSettled Bets</option>

                            </select>
                          </div>
                          <div className="col-md-2">
                            {/* <label>Select option:</label> */}
                            <ClientMultiselect
                              value={selectOption}
                              onChange={setSelectOption}
                              onSelect={(u) => setSelectOption(u.username)}
                              showNoResults={false}
                            />
                          </div>
                          <div className="col-md-2">
                            {/* <label>Select:</label> */}
                            <select
                              className="form-control"
                              value={selectValue}
                              onChange={(e) => setSelectValue(e.target.value)}
                            >
                              <option value="">Select</option>
                              {CASINO_VENDORS.map((v) => (
                                <option key={v} value={v}>{v}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <button type="submit" className="btn btn-primary">
                              Submit
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>

              {/* Table Section */}
              <div className="row">
                <div className="col-12">
                  <div className="">
                    <div className="">
                      {/* Show Entries and Search */}
                      <div className="row mt-4">
                        <div className="col-sm-12 col-md-6">
                          <div id="tickets-table_length" className="dataTables_length">
                            <label className="d-inline-flex align-items-center">
                              Show&nbsp;
                              <select
                                className="custom-select custom-select-sm"
                                value={entriesPerPage}
                                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                              >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="250">250</option>
                                <option value="500">500</option>
                                <option value="750">750</option>
                                <option value="1000">1000</option>
                              </select>
                              &nbsp;entries
                            </label>
                          </div>
                        </div>
                        <div className="col-sm-12 col-md-6">
                          <div id="tickets-table_filter" className="dataTables_filter text-md-right">
                            <label className="d-inline-flex align-items-center">
                              Search:
                              <input
                                name="search"
                                type="search"
                                placeholder="Search..."
                                className="form-control form-control-sm ml-2 form-control py-1"
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                              />
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Table */}
                      <div className="table-responsive mb-0 mt-4">
                        <div className="table no-footer table-responsive-sm">
                          <table
                            id="casinoReportTbl"
                            role="table"
                            aria-busy={loading}
                            aria-colcount="7"
                            className="table b-table table-striped table-bordered"
                          >
                            <thead role="rowgroup" className="">
                              <tr role="row" className="">
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="1"
                                  className="position-relative"
                                >
                                  <div>Game Name</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="2"
                                  className="position-relative"
                                >
                                  <div>Type</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="3"
                                  className="position-relative text-right"
                                >
                                  <div>Amount</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="4"
                                  className="position-relative text-right"
                                >
                                  <div>Total</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="5"
                                  className="position-relative"
                                >
                                  <div>Date</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="6"
                                  className="position-relative"
                                >
                                  <div>Round Id</div>
                                </th>
                                <th
                                  role="columnheader"
                                  scope="col"
                                  tabIndex="0"
                                  aria-colindex="7"
                                  className="position-relative"
                                >
                                  <div>Transaction Id</div>
                                </th>
                              </tr>
                            </thead>
                            <tbody role="rowgroup">
                              {loading ? (
                                <tr role="row" className="b-table-empty-row">
                                  <td colSpan="7" role="cell" className="">
                                    <div role="alert" aria-live="polite">
                                      <div className="text-center my-2">Loading...</div>
                                    </div>
                                  </td>
                                </tr>
                              ) : tableData.length === 0 ? (
                                <tr role="row" className="b-table-empty-row">
                                  <td colSpan="7" role="cell" className="">
                                    <div role="alert" aria-live="polite">
                                      <div className="text-center my-2">No data available in table</div>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                tableData.map((row, index) => (
                                  <tr key={row.id || index} role="row" className="">
                                    <td aria-colindex="1" role="cell" className="">
                                      {row.gameName || '-'}
                                    </td>
                                    <td aria-colindex="2" role="cell" className="">
                                      {row.type || '-'}
                                    </td>
                                    <td aria-colindex="3" role="cell" className="text-right">
                                      {row.amount || '-'}
                                    </td>
                                    <td aria-colindex="4" role="cell" className="text-right">
                                      {row.total || '-'}
                                    </td>
                                    <td aria-colindex="5" role="cell" className="">
                                      {row.date || '-'}
                                    </td>
                                    <td aria-colindex="6" role="cell" className="">
                                      {row.roundId || '-'}
                                    </td>
                                    <td aria-colindex="7" role="cell" className="">
                                      {row.transactionId || '-'}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pagination */}
                      <div className="row pt-3">
                        <div className="col">
                          <div className="dataTables_paginate paging_simple_numbers float-right">
                            <ul className="pagination pagination-rounded mb-0">
                              <ul
                                role="menubar"
                                aria-label="Pagination"
                                className="pagination dataTables_paginate paging_simple_numbers my-0 b-pagination justify-content-end"
                              >
                                <li role="presentation" className="page-item disabled">
                                  <span role="menuitem" aria-label="Go to first page" className="page-link">«</span>
                                </li>
                                <li role="presentation" className="page-item disabled">
                                  <span role="menuitem" aria-label="Go to previous page" className="page-link">‹</span>
                                </li>
                                <li role="presentation" className="page-item active">
                                  <button
                                    role="menuitemradio"
                                    type="button"
                                    aria-label="Go to page 1"
                                    className="page-link"
                                  >
                                    1
                                  </button>
                                </li>
                                <li role="presentation" className="page-item disabled">
                                  <span role="menuitem" aria-label="Go to next page" className="page-link">›</span>
                                </li>
                                <li role="presentation" className="page-item disabled">
                                  <span role="menuitem" aria-label="Go to last page" className="page-link">»</span>
                                </li>
                              </ul>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default CasinoReport

